import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useCreateDoc,
  useDeleteDoc,
} from "@/lib/hooks/useFrappeQuery";
import { useAuthStore } from "@/lib/store/authStore";
import { getERPClient } from "@/lib/erp/client";
import { uploadAttachment } from "@/lib/erp/docs";

const DOCTYPE = "Storefront Payment Method";

export interface PaymentMethod {
  name: string;
  payment_type: "Bank Transfer" | "Stripe" | "COD" | "PaySolution";
  enabled: 0 | 1;
  display_order: number;
  // Bank Transfer fields
  qr_code_image?: string;
  bank_name?: string;
  account_name?: string;
  account_number?: string;
  // Stripe fields (Password type — REST returns "*****" when set)
  stripe_publishable_key?: string;
  stripe_secret_key?: string;
  stripe_webhook_secret?: string;
  // COD fields
  cod_max_amount?: number;
  // PaySolution fields (Password type for secret_key)
  paysolution_api_key?: string;
  paysolution_secret_key?: string;
  paysolution_merchant_id?: string;
  paysolution_terminal_id?: string;
  paysolution_environment?: "Sandbox" | "Production";
  paysolution_postback_url?: string;
  // Per-store scoping: which storefront this method shows on.
  // Value is a storefront_key (e.g. "0j85q3282r", "subscrub"); blank = all
  // storefronts of this company. Read by get_payment_methods →
  // resolve_tenant_methods on the backend.
  tenant?: string;
  // Meta
  company?: string;
  creation?: string;
  modified?: string;
}

/**
 * List all payment methods for the current tenant.
 * Uses fields: ["*"] to fetch all detail fields (bank_name, account_number, etc.)
 * needed by the card summaries and edit form.
 * No manual company filter — tenant_isolation.py handles it globally.
 */
export function usePaymentMethods() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<PaymentMethod[]>({
    queryKey: ["list", DOCTYPE],
    queryFn: async () => {
      const client = getERPClient();
      return client.list<PaymentMethod>({
        doctype: DOCTYPE,
        fields: ["*"],
        order_by: "display_order asc",
      });
    },
    enabled: isAuthenticated,
  });
}

/**
 * Create a new payment method.
 */
export function useCreatePaymentMethod() {
  return useCreateDoc<PaymentMethod>(DOCTYPE);
}

/**
 * Update a payment method by name.
 * Uses custom useMutation (not useUpdateDoc) because useUpdateDoc binds
 * `name` at hook instantiation — can't reuse for different docs in a list.
 */
export function useUpdatePaymentMethod() {
  const queryClient = useQueryClient();

  return useMutation<
    PaymentMethod,
    Error,
    { name: string; data: Record<string, any> }
  >({
    mutationFn: async ({ name, data }) => {
      const client = getERPClient();
      return client.update<PaymentMethod>({
        doctype: DOCTYPE,
        name,
        data,
      });
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["get", DOCTYPE, variables.name] });
      queryClient.invalidateQueries({ queryKey: ["list", DOCTYPE] });
    },
  });
}

/**
 * Delete a payment method.
 */
export function useDeletePaymentMethod() {
  return useDeleteDoc(DOCTYPE);
}

/**
 * Upload a QR code image and attach it to an existing payment method.
 * Uses the shared uploadAttachment() utility (mode-aware).
 *
 * Requires the document to already exist — call useCreatePaymentMethod first,
 * then upload the image in a second step.
 */
export function useUploadPaymentQR() {
  const queryClient = useQueryClient();

  return useMutation<
    { file_url: string; file_name: string; name: string },
    Error,
    { docName: string; file: File }
  >({
    mutationFn: async ({ docName, file }) => {
      return uploadAttachment({
        doctype: DOCTYPE,
        name: docName,
        file,
      });
    },
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["get", DOCTYPE, variables.docName] });
      queryClient.invalidateQueries({ queryKey: ["list", DOCTYPE] });
    },
  });
}

/**
 * Reorder payment methods by updating display_order on each.
 * Accepts an array of { name, display_order } pairs.
 */
export function useReorderPaymentMethods() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { name: string; display_order: number }[]>({
    mutationFn: async (items) => {
      const client = getERPClient();
      await Promise.all(
        items.map((item) =>
          client.update({
            doctype: DOCTYPE,
            name: item.name,
            data: { display_order: item.display_order },
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list", DOCTYPE] });
    },
  });
}
